/**
 * 契約日 編集モーダル。
 * 開始日を手動指定すると終了日 (開始日 + 1年 − 1日) は自動再計算される。
 */
import { contractEndFromStart } from '@goenhub/shared';
import { CalendarClock } from 'lucide-react';
import { useEffect, useState } from 'react';


import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { useUpdateSaOnboarding, type SaAgentRecord } from '@/hooks/useSaAgents';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

interface SaContractDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent: SaAgentRecord | null;
}

export function SaContractDialog({
  open,
  onOpenChange,
  agent,
}: SaContractDialogProps) {
  const updateMutation = useUpdateSaOnboarding();
  const [startDate, setStartDate] = useState('');
  const [years, setYears] = useState(1);

  useEffect(() => {
    if (!open || !agent) return;
    setStartDate(agent.saOnboarding?.contractStartDate ?? '');
    setYears(agent.saOnboarding?.contractYears ?? 1);
  }, [open, agent]);

  const endPreview = DATE_RE.test(startDate)
    ? contractEndFromStart(startDate, years)
    : '—';

  const handleSave = async () => {
    if (!agent) return;
    if (!DATE_RE.test(startDate)) {
      toast.error('開始日を選択してください');
      return;
    }
    try {
      await updateMutation.mutateAsync({
        uid: agent.uid,
        contractStartDate: startDate,
        contractYears: years,
      });
      toast.success('契約日を更新しました');
      onOpenChange(false);
    } catch (err) {
      toast.error(
        `更新に失敗しました: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <CalendarClock className="inline size-4 mr-1" />
            契約日の編集
          </DialogTitle>
          <DialogDescription>
            開始日を手動指定します。終了日は開始日 + 1年 − 1日で自動計算されます。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="sa-contract-start">契約開始日</Label>
              <Input
                id="sa-contract-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="sa-contract-years">契約年数</Label>
              <Input
                id="sa-contract-years"
                type="number"
                min={1}
                max={10}
                value={years}
                onChange={(e) =>
                  setYears(Math.max(1, Math.floor(Number(e.target.value) || 1)))
                }
              />
            </div>
          </div>
          <div className="text-sm text-[var(--text-secondary)]">
            契約終了日 (自動):{' '}
            <span className="font-semibold text-[var(--text-primary)]">
              {endPreview}
            </span>
          </div>
          <p className="text-xs text-[var(--text-muted)]">
            通常は「登録完了した月の翌月 1 日」が自動設定されます。ここで指定すると手動指定として記録されます。
          </p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={updateMutation.isPending}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSave}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? '保存中...' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
